// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Filters section
// - Inputs/select: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - No business/API logic changed

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

const ReportStudentBacklogsSummary = () => {
  const navigate = useNavigate();

  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    whiteSpace: "nowrap",
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  // ===== Original state/logic (UNCHANGED) =====
  const [summaryData, setSummaryData] = useState([]);
  const [campusList, setCampusList] = useState([]);
  const [subjectNameCodeList, setSubjectNameCodeList] = useState([]);
  const [semesterList, setSemesterList] = useState([]);
  const [gradeList, setGradeList] = useState([]);

  const [selectedCampus, setSelectedCampus] = useState("");
  const [selectedSubjectNameCode, setSelectedSubjectNameCode] = useState("");
  const [selectedSubjectNameCodeDisplay, setSelectedSubjectNameCodeDisplay] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [error, setError] = useState("");
  const [logincampusCode, setCampusCode] = useState("");
  const token = localStorage.getItem("auth");

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const campusCode = decodedToken.campusCode;
        setCampusCode(campusCode);
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchDropdownFilters = async () => {
      try {
        const campusResponse = await axios.get(
          `${API_URL}/api/GetCampuses?CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const subjectNameCodeResponse = await axios.get(
          `${API_URL}/api/GetSubjectNameCodes?CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const semesterResponse = await axios.get(
          `${API_URL}/api/GetSemesters?CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const gradeResponse = await axios.get(
          `${API_URL}/api/GetGrades?CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setCampusList(["All Campuses", ...(campusResponse.data || []).map((c) => c.Campus)]);
        setSubjectNameCodeList(["All Subjects", ...(subjectNameCodeResponse.data || []).map((s) => s.SubjectNameCode)]);
        setSemesterList(["All Semesters", ...(semesterResponse.data || []).map((s) => s.Semester)]);
        setGradeList(["All Grades", ...(gradeResponse.data || []).map((g) => g.Grade)]);
      } catch (err) {
        console.error("Error fetching dropdown filters:", err);
        setError("Failed to fetch dropdown filters.");
      }
    };

    if (logincampusCode) fetchDropdownFilters();
  }, [logincampusCode, token]);

  const fetchSummaryData = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/ReportBacklogByStudentSummary?CampusCode=${encodeURIComponent(logincampusCode)}`,
        {
          params: {
            campus: selectedCampus === "All Campuses" ? "All Campuses" : selectedCampus || "",
            subjectNameCode: selectedSubjectNameCode === "All Subjects" ? "All Subjects" : selectedSubjectNameCode,
            semester: selectedSemester === "All Semesters" ? "All Semesters" : selectedSemester || "",
            grade: selectedGrade === "All Grades" ? "All Grades" : selectedGrade || "",
            campusCode: logincampusCode,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSummaryData(response.data || []);
      setError("");
    } catch (err) {
      console.error("Error fetching summary data:", err);
      setError("Failed to fetch summary data.");
    }
  };

  useEffect(() => {
    if (logincampusCode) fetchSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode]);

  useEffect(() => {
    if (selectedCampus !== "" || selectedSubjectNameCode !== "" || selectedSemester !== "" || selectedGrade !== "") {
      fetchSummaryData();
    } else {
      setSummaryData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampus, selectedSubjectNameCode, selectedSemester, selectedGrade]);

  const handleSubjectNameCodeChange = (value) => {
    const extractedCode = value.match(/<([^>]+)>/)?.[1] || "";
    setSelectedSubjectNameCode(extractedCode);
    setSelectedSubjectNameCodeDisplay(value);
  };

  const handleResetFilters = () => {
    setSelectedCampus("");
    setSelectedSubjectNameCode("");
    setSelectedSubjectNameCodeDisplay("");
    setSelectedSemester("");
    setSelectedGrade("");
    setSummaryData([]);
  };

  const handleNavigateHome = () => navigate("/home");
  const handleLogout = () => navigate("/");

  const renderTableHeader = () => {
    // ✅ Keep your existing logic, just use themed header row style
    if (selectedCampus) {
      return (
        <TableRow sx={theadRowSx}>
          <TableCell>#</TableCell>
          <TableCell>Campus</TableCell>
          <TableCell>Number of Backlogs (By Campus)</TableCell>
        </TableRow>
      );
    }
    if (selectedSubjectNameCode) {
      return (
        <TableRow sx={theadRowSx}>
          <TableCell>#</TableCell>
          <TableCell>Subject Name Code</TableCell>
          <TableCell>Number of Backlogs (By Subject)</TableCell>
        </TableRow>
      );
    }
    if (selectedSemester) {
      return (
        <TableRow sx={theadRowSx}>
          <TableCell>#</TableCell>
          <TableCell>Semester</TableCell>
          <TableCell>Number of Backlogs (By Semester)</TableCell>
        </TableRow>
      );
    }
    if (selectedGrade) {
      return (
        <TableRow sx={theadRowSx}>
          <TableCell>#</TableCell>
          <TableCell>Grade</TableCell>
          <TableCell>Number of Backlogs (By Grade)</TableCell>
        </TableRow>
      );
    }
    return (
      <TableRow sx={theadRowSx}>
        <TableCell>#</TableCell>
        <TableCell>Campus</TableCell>
        <TableCell>Subject Code</TableCell>
        <TableCell>Subject Name Code</TableCell>
        <TableCell>Semester</TableCell>
        <TableCell>Grade</TableCell>
        <TableCell>Number of Backlogs</TableCell>
      </TableRow>
    );
  };

  const renderTableBody = () => {
    let totalCount = 0;

    const rows = summaryData.map((row, index) => {
      if (selectedCampus) {
        totalCount += row.NumberOfBacklogsByCampus || 0;
        return (
          <TableRow key={index} sx={zebraRowSx(index)}>
            <TableCell>{index + 1}</TableCell>
            <TableCell>{row.Campus}</TableCell>
            <TableCell>{row.NumberOfBacklogsByCampus}</TableCell>
          </TableRow>
        );
      }
      if (selectedSubjectNameCode) {
        totalCount += row.NumberOfBacklogsBySubject || 0;
        return (
          <TableRow key={index} sx={zebraRowSx(index)}>
            <TableCell>{index + 1}</TableCell>
            <TableCell>{row.SubjectNameCode}</TableCell>
            <TableCell>{row.NumberOfBacklogsBySubject}</TableCell>
          </TableRow>
        );
      }
      if (selectedSemester) {
        totalCount += row.NumberOfBacklogsBySemester || 0;
        return (
          <TableRow key={index} sx={zebraRowSx(index)}>
            <TableCell>{index + 1}</TableCell>
            <TableCell>{row.Semester}</TableCell>
            <TableCell>{row.NumberOfBacklogsBySemester}</TableCell>
          </TableRow>
        );
      }
      if (selectedGrade) {
        totalCount += row.NumberOfBacklogsByGrade || 0;
        return (
          <TableRow key={index} sx={zebraRowSx(index)}>
            <TableCell>{index + 1}</TableCell>
            <TableCell>{row.Grade}</TableCell>
            <TableCell>{row.NumberOfBacklogsByGrade}</TableCell>
          </TableRow>
        );
      }

      totalCount += row.NumberOfBacklogs || 0;
      return (
        <TableRow key={index} sx={zebraRowSx(index)}>
          <TableCell>{index + 1}</TableCell>
          <TableCell>{row.Campus}</TableCell>
          <TableCell>{row.SubjectCode}</TableCell>
          <TableCell>{row.SubjectNameCode}</TableCell>
          <TableCell>{row.Semester}</TableCell>
          <TableCell>{row.Grade}</TableCell>
          <TableCell>{row.NumberOfBacklogs}</TableCell>
        </TableRow>
      );
    });

    // Total row (keep your structure, just theme it a bit)
    rows.push(
      <TableRow
        key="total"
        sx={{
          backgroundColor: "rgba(240,120,40,0.06)",
          "& td": { borderTop: "1px solid rgba(25,118,210,0.12)", py: 1 },
        }}
      >
        <TableCell colSpan={selectedCampus ? 2 : 2} align="right">
          <strong>Total Count</strong>
        </TableCell>
        <TableCell>
          <strong>{totalCount}</strong>
        </TableCell>
      </TableRow>
    );

    return rows;
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Backlog Summary
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter by campus, subject, semester, or grade
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Filters */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Select
                  value={selectedCampus}
                  onChange={(e) => setSelectedCampus(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, minWidth: 220, flex: 1 }}
                >
                  <MenuItem value="">
                    <em>Select a Campus</em>
                  </MenuItem>
                  {campusList.map((campus, index) => (
                    <MenuItem key={index} value={campus}>
                      {campus}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={selectedSubjectNameCodeDisplay}
                  onChange={(e) => handleSubjectNameCodeChange(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, minWidth: 240, flex: 1 }}
                >
                  <MenuItem value="">
                    <em>Select a Subject</em>
                  </MenuItem>
                  {subjectNameCodeList.map((code, index) => (
                    <MenuItem key={index} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, minWidth: 200, flex: 1 }}
                >
                  <MenuItem value="">
                    <em>Select a Semester</em>
                  </MenuItem>
                  {semesterList.map((semester, index) => (
                    <MenuItem key={index} value={semester}>
                      {semester}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, minWidth: 200, flex: 1 }}
                >
                  <MenuItem value="">
                    <em>Select a Grade</em>
                  </MenuItem>
                  {gradeList.map((grade, index) => (
                    <MenuItem key={index} value={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </Select>

                <Button variant="contained" sx={primaryBtnSx} onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {/* Table */}
          <Paper elevation={0} sx={tableWrapSx}>
              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
            <TableContainer component="div">
            <Box sx={{ p: 2 }}>
              {summaryData.length === 0 ? (
                <Typography sx={{ textAlign: "center", py: 3, opacity: 0.8 }}>
                  No data to display
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>{renderTableHeader()}</TableHead>
                  <TableBody>{renderTableBody()}</TableBody>
                </Table>
              )}
            </Box>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportStudentBacklogsSummary;
